import { LocalDate } from "@js-joda/core";
import { useQuery } from "@tanstack/react-query";
import React from "react";
import useApi from "../../../shared/hooks/useApi";
import {
  contributeAiCapabilityParamOption,
  declareAiCapability,
  useProvideAiCapability,
} from "../../ai-omnibar/ai-capability-registry";
import { declareAiTabDestination } from "../../ai-omnibar/ai-tab-destinations";

// The tab saying it is somewhere to go. Care & Task management opens on Tasks, so this tab is only
// reachable by name if it says so itself.
declareAiTabDestination({
  id: "tasksV2.open",
  title: "Open the Tasks 2.0 tab",
  stateName: "app.patients.careAndTaskManagement.tasksV2",
  opens: "page:tasksV2",
  description:
    "Opens Patients > Care & Task management > Tasks 2.0, the redesigned tasks list, with a documents drawer and the document review screen.",
  location: "the tab bar of Care & Task management",
  permissionKey: "view_patients_section_upcoming_tasks",
  examples: ["open tasks 2.0"],
});
import type { QueryParams } from "../../../shared/hooks/useQueryParams";
import type {
  ContractTypeId,
  OfficeId,
  PatientTaskTemplateId,
} from "../../../shared/schema/schema";
import type { PatientTaskNewStatus, TasksV2Filters } from "./hooks/useTasksV2Filters";

/**
 * The planner's yyyy-MM-dd as the date type the filters hold.
 *
 * A date it cannot read stops the step and says so, rather than setting a filter to something the
 * page will throw on the moment it renders.
 */
function asLocalDate(text: string): LocalDate {
  try {
    return LocalDate.parse(text);
  } catch {
    throw new Error(`Could not read the date ${text}.`);
  }
}

/**
 * What the Tasks 2.0 tab can do for the AI omnibar.
 *
 * Its caregiver and patient filters are deliberately absent: the page fills those from a typeahead
 * search rather than a list, so there is no set of options to offer and no way to turn a name into
 * the id the filter wants without guessing. Better to leave those to the person than to half-answer.
 */
declareAiCapability({
  id: "tasksV2.filter",
  title: "Filter the Tasks 2.0 list",
  effect: "prefill",
  description:
    "Sets the filters on Patients > Care & Task management > Tasks 2.0: the scheduled date range, the task's status, the task template, contract and office. Anything left out keeps what the page already has. Only works while that tab is open.",
  location: "the filter bar of the Tasks 2.0 tab",
  scope: "page:tasksV2",
  permissionKey: "view_patients_section_upcoming_tasks",
  params: [
    {
      name: "scheduledFrom",
      type: "date",
      required: false,
      description: "Earliest scheduled day to include, as yyyy-MM-dd.",
    },
    {
      name: "scheduledTo",
      type: "date",
      required: false,
      description: "Latest scheduled day to include, as yyyy-MM-dd.",
    },
    {
      name: "status",
      type: "enum",
      required: false,
      multiple: true,
      description: "Whether the task is approved, completed or incompleted.",
      options: [
        { value: "APPROVED", label: "Approved" },
        { value: "COMPLETED", label: "Completed" },
        { value: "INCOMPLETED", label: "Incompleted" },
      ],
    },
    {
      name: "taskTemplate",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to tasks made from these templates.",
      // Contributed below from the same list the page's own select uses.
    },
    {
      name: "contract",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to patients on these contracts.",
    },
    {
      name: "office",
      type: "enum",
      required: false,
      multiple: true,
      description: "Limit to these offices.",
    },
  ],
  examples: [
    "incompleted tasks scheduled this week",
    "approved tasks for the europe office",
    "tasks 2.0 on the france contract",
  ],
});

function useContributedOptions(
  paramName: string,
  rows: readonly { id: string | number; name: string }[] | undefined
): void {
  const key = (rows ?? []).map((row) => `${row.id}:${row.name}`).join("|");

  React.useEffect(() => {
    const releases = (rows ?? []).map((row) =>
      contributeAiCapabilityParamOption("tasksV2.filter", paramName, {
        value: String(row.id),
        label: row.name,
      })
    );

    return () => releases.forEach((release) => release());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paramName, key]);
}

export function useTasksV2AiCapabilities(params: {
  filters: QueryParams<TasksV2Filters>;
  /** The page's own template list, so the options offered are the ones its select shows. */
  templates: readonly { id: number; title: string }[] | undefined;
}): void {
  const { filters, templates } = params;
  const { queries } = useApi();

  const filtersRef = React.useRef(filters);
  filtersRef.current = filters;

  const offices = useQuery(queries.office.list());
  const contracts = useQuery(queries.contract.list());

  useContributedOptions("office", offices.data?.offices);
  useContributedOptions("contract", contracts.data?.contractTypes);
  useContributedOptions(
    "taskTemplate",
    React.useMemo(
      () => (templates ?? []).map((template) => ({ id: template.id, name: template.title })),
      [templates]
    )
  );

  // Not available until the lists have arrived: an empty option list turns a real filter into a
  // free-text search, so "europe" would fall through to nothing instead of the office filter.
  useProvideAiCapability(
    "tasksV2.filter",
    (args, context) => {
      const applied: string[] = [];
      const asList = (value: unknown) => (Array.isArray(value) ? value : [value]);
      const label = (name: string) => context.labels[name] ?? String(args[name]);
      // Typed, not a bag of unknowns cast on the way out: the cast that used to sit on setValues
      // is what let a yyyy-MM-dd string reach a LocalDate field and break the page on render.
      const values: Partial<TasksV2Filters> = { page: 1 };

      // As LocalDate, which is what this page's filters hold. Handing them the yyyy-MM-dd string
      // the planner sends put a string where a date was expected, and the page threw on the first
      // render after the filter was set - the omnibar reported the step done over a broken screen.
      for (const name of ["scheduledFrom", "scheduledTo"] as const) {
        if (args[name] === undefined) {
          continue;
        }

        values[name] = asLocalDate(String(args[name]));
        applied.push(`${name === "scheduledFrom" ? "from" : "to"} ${String(args[name])}`);
      }

      if (args.status !== undefined) {
        const statuses = asList(args.status).map((one) => String(one).toUpperCase());

        for (const status of statuses) {
          if (!["APPROVED", "COMPLETED", "INCOMPLETED"].includes(status)) {
            throw new Error(`A task is approved, completed or incompleted - not ${status}.`);
          }
        }

        values.statuses = statuses as PatientTaskNewStatus[];
        applied.push(statuses.map((status) => status.toLowerCase()).join(" and "));
      }

      // One at a time rather than a loop over pairs: each list holds a differently branded id, and
      // a loop can only satisfy the compiler by casting away the very thing being checked.
      if (args.taskTemplate !== undefined) {
        values.taskTemplateIds = asList(args.taskTemplate).map(
          (one) => Number(one) as PatientTaskTemplateId
        );
        applied.push(label("taskTemplate"));
      }

      if (args.contract !== undefined) {
        values.contractTypeIds = asList(args.contract).map((one) => Number(one) as ContractTypeId);
        applied.push(label("contract"));
      }

      if (args.office !== undefined) {
        values.officeIds = asList(args.office).map((one) => Number(one) as OfficeId);
        applied.push(label("office"));
      }

      filtersRef.current.setValues(values);

      return applied.length === 0
        ? "Re-applied the task filters"
        : `Showing tasks: ${applied.join(", ")}`;
    },
    { enabled: offices.isSuccess && contracts.isSuccess }
  );
}
